<template>
  <ModalDialog
    ref="dialog"
    title="Request Pricing Update"
    :submitting="submitting"
    :submitDisabled="submitDisabled"
    submitTitle="Request Approval"
    icon="fa-money-check-edit-alt"
    @closed="onClosed"
    @submitted="onSubmitted">

    <FormGroup v-bind:form="dialogForm" :labelColumnSize="3">
      <Spinner v-if="loading" />
      <span class="text-danger" v-if="loadingError">{{loadingError}}</span>

      <NotificationBar type="warning" v-if="appendPriceRequest && !appendPriceRequestError">
        <NotificationBarTitle>Note: </NotificationBarTitle>Price updates will be merged with the price request group <strong>#{{appendPriceRequest.sequenceNumber}}</strong>
      </NotificationBar>
      <NotificationBar type="error" v-if="appendPriceRequest && appendPriceRequestError">
        <NotificationBarTitle>{{appendPriceRequestError}}</NotificationBarTitle>
      </NotificationBar>

      <template v-if="!loading">
        <FormRow label="Stock">
          <FormStaticLabel>{{updateStockData.code}} - {{updateStockData.name}}</FormStaticLabel>
        </FormRow>
        <FormRow label="Price Update Type">
          <LabelPriceRequestTypeBadge :type="PriceRequestTypes.sales" />
        </FormRow>
        <FormRow label="Price Request Group">
          <LinkPriceRequest :id="appendPriceRequest.id" :name="appendPriceRequest.sequenceNumber ? '#'+appendPriceRequest.sequenceNumber : 'Unknown'" v-if="appendPriceRequest" />
          <FormStaticLabel v-if="!appendPriceRequest">New Price Request</FormStaticLabel>
        </FormRow>
        <FormRow label="Update Reason">
          <FormInputTextArea ref="updateReasonEntry" placeholder="Update Reason" v-model="requestData.requestReason" field="requestReason" />
        </FormRow>

        <FormHorizontalRule />
        
        <h3>Price Changes</h3>
        <StaticDataTable class="static-table-list" variant="table-sm">
          <template v-slot:header>
            <tr>
              <th>Name</th>
              <th>Active</th>
              <th>Price</th>
              <th>Min Size Charge</th>
              <th>Min Price Per Item</th>
              <th>Min Price Per Group</th>
              <th>Min Invoice Charge</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="(priceChange, index) in priceChangeList" :key="index">
              <td>{{priceChange.display}}</td>
              <td>
                <span v-html="displayPriceActiveChange(priceChange.prev ? priceChange.prev.isActive : null, priceChange.new.isActive)"></span>
              </td>
              <td>
                <span v-html="displayPriceChange(priceChange.prev ? priceChange.prev.price : 0, priceChange.new.price, updateStockData.uomType)"></span>
              </td>
              <td>
                <span v-html="displayValueChange(priceChange.prev ? priceChange.prev.minChargeQuantity : 0, priceChange.new.minChargeQuantity)"></span>
              </td>
              <td>
                <span v-html="displayPriceChange(priceChange.prev ? priceChange.prev.minChargeItemPrice : 0, priceChange.new.minChargeItemPrice)"></span>
              </td>
              <td>
                <span v-html="displayPriceChange(priceChange.prev ? priceChange.prev.minChargePrice : 0, priceChange.new.minChargePrice)"></span>
              </td>
              <td>
                <span v-html="displayPriceChange(priceChange.prev ? priceChange.prev.minChargeInvoice : 0, priceChange.new.minChargeInvoice)"></span>
              </td>
            </tr>
          </template>
        </StaticDataTable>
        <p class="text-center font-italic" v-if="priceChangeList.length == 0">No pricing updates found</p>
        <br />
        <p class="text-muted font-italic">Note: Price updates will be sent for approval. Updates will not be active until approved</p>
      </template>
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'
import StaticDataTable from '@/components/table/staticDataTable'
import PriceRequestMixin from '@/modules/pricing/mixins/priceRequestMixin'
import LabelPriceRequestTypeBadge from '@/modules/pricing/components/labelPriceRequestTypeBadge'

import { InventoryStockTypes, PriceRequestTypes, PricingRequestStatusTypes } from '@/store/types'

export default {
  name: "SalesPriceRequestUpdateDialog",
  mixins: [
    ModalDialogMixin,
    form,
    PriceRequestMixin
  ],
  components: {
    ModalDialog,
    StaticDataTable,
    LabelPriceRequestTypeBadge
  },
  props: {
  },
  data() {
    return {
      requestData: {},
      currentStockData: {},
      updateStockData: {},
      priceChangeList: [],
      salesPriceRequestList: [],
      appendPriceRequest: null,
      appendPriceRequestError: '',
      dialogForm: {},
      loading: false,
      loadingError: '',
      submitting: false
    }
  },
  created() {
    this.PriceRequestTypes = PriceRequestTypes
  },
  mounted() {
  },
  computed: {
    submitDisabled() {
      return this.priceChangeList.length == 0 || this.appendPriceRequestError != ''
    },
  },
  methods: {
    show(stockData) {
      // Reset form
      this.requestData = {}
      this.currentStockData = { }
      this.updateStockData = { }
      this.priceChangeList = []
      this.dialogForm = { }
      this.submitting = false
      this.loading = false
      this.loadingError = ''
      this.appendPriceRequest = null
      this.appendPriceRequestError = ''

      // Stock Data HAS to exist
      if(stockData != null) {
        this.updateStockData = _.cloneDeep(stockData)       

        // Get the current stock pricing values
        this.loading = true
        Promise.all([
          this.$apiService.inventoryService.getStock(stockData.id),
          this.$apiService.pricingService.getPriceRequestList({ type: PriceRequestTypes.sales, status: [PricingRequestStatusTypes.draft, PricingRequestStatusTypes.pendingApproval, PricingRequestStatusTypes.pendingSchedule, PricingRequestStatusTypes.processing, PricingRequestStatusTypes.generatingPriceLists] })
        ])
        .then(response => {
          this.$data.currentStockData = response[0].data
          this.$data.salesPriceRequestList = response[1].data

          // Find the existing sales price request in the list we'll append to
          if(this.$data.salesPriceRequestList.length > 0) {
            this.$data.appendPriceRequest = this.$data.salesPriceRequestList[0]

            // If not in draft mode, show an error
            if(this.$data.appendPriceRequest.status != PricingRequestStatusTypes.draft) {
              this.appendPriceRequestError = 'Current price request group is not in draft mode. Move it to draft before continuing'
            }

            this.requestData.requestReason = this.appendPriceRequest.reason || ''
          }

          // Calculate the list of prices that have actually changed
          this.generatePriceChangeList()
        })
        .catch(err => {
          this.loadingError = 'Failed to load stock data'
        })
        .finally(() => {
          this.$data.loading = false

          this.$nextTick(() => {
          if(this.$refs.updateReasonEntry)
            this.$refs.updateReasonEntry.focus()
          })
        })

      } else {
        this.loadingError = 'No data provided'
      }

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      this.submitting = true

      // Update records that have changed
      let dataUpdateList = this.priceChangeList.map(priceChange => priceChange.new)

      var updateData = {
        salesPriceList: dataUpdateList,
        requestReason: this.$data.requestData.requestReason
      }

      // Set the price request object to append this update too
      if(this.appendPriceRequest)
        updateData.stockPriceRequestId = this.appendPriceRequest.id

      this.$apiService.pricingService.updateStockSalesPrices(this.$data.updateStockData.id, updateData)
      .then(response => {
        this.modalClosed(true)
        this.hide()
      })
      .catch(err => {
        this.$data.dialogForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.submitting = false
      })
    },

    // Calculate the list of prices that have actually changed
    generatePriceChangeList() {
      try {
        this.priceChangeList = []

        var glassDisplayCB = (salesPrice) => { return salesPrice.name }

        if(this.$data.currentStockData.type == InventoryStockTypes.glass) {

          this.addPriceChangeList(this.priceChangeList, {
            currentList: this.$data.currentStockData.glassSalesPriceList,
            newList: this.$data.updateStockData.glassSalesPriceList,
            key: ['stockId', 'stockPriceGroupId'],
            fields: ['isActive', 'price', 'minChargeItemPrice', 'minChargePrice', 'minChargeQuantity', 'minChargeInvoice'],
            displayCallback: glassDisplayCB
          })
        }

        else if(this.$data.currentStockData.type == InventoryStockTypes.glassService) {

          this.$data.currentStockData.serviceSalesPriceList.forEach(priceGroup => {

            var updateList = this.$data.updateStockData.serviceSalesPriceList.find(updateGroup => updateGroup.stockPriceGroupId == priceGroup.stockPriceGroupId)
            if(updateList) {
              this.addPriceChangeList(this.priceChangeList, {
                currentList: priceGroup.servicePriceRanges,
                newList: updateList.servicePriceRanges,
                key: ['stockId', 'stockPriceGroupId', 'servicePriceRangeId'],
                fields: ['isActive', 'price', 'minChargeItemPrice', 'minChargePrice', 'minChargeInvoice'],
                displayCallback: (salesPrice) => { return salesPrice.name + ' - ' + priceGroup.name }
              })
            }
          })
        }

        else if(this.$data.currentStockData.type == InventoryStockTypes.general) {

          this.addPriceChangeList(this.priceChangeList, {
            currentList: this.$data.currentStockData.generalSalesPriceList,
            newList: this.$data.updateStockData.generalSalesPriceList,
            key: ['stockId'],
            fields: ['isActive', 'price', 'minChargeItemPrice', 'minChargePrice'],
            displayCallback: glassDisplayCB
          })
        }

        else if(this.$data.currentStockData.type == InventoryStockTypes.assembly) {

          this.addPriceChangeList(this.priceChangeList, {
            currentList: this.$data.currentStockData.assemblySalesPriceList,
            newList: this.$data.updateStockData.assemblySalesPriceList,
            key: ['stockId'],
            fields: ['isActive', 'price', 'minChargeItemPrice', 'minChargePrice'],
            displayCallback: glassDisplayCB
          })
        }

      } catch(err) {
        console.error(err)
      }
    },    
  }
}
</script>